import React from "react";
import classNames from "classnames";
import { gql, useQuery } from "@apollo/client";

import Spinner from "components/Spinner";

import { SQUAREFOOT_HQ_COORDS } from "config";
import { Layer, Source } from "@vis.gl/react-maplibre";
import * as maplibre from "maplibre-gl";
import SubmarketNeighborhoodSelector from "./SubmarketNeighborhoodSelector";
import styles from "./style.module.scss";
import MarketSelector from "./MarketSelector";
import {
  market_market,
  market_market_cities,
  market_market_submarkets,
  market_market_submarkets_neighborhoods,
} from "./types/market";
import BaseMap, {
  FillPaintOptions,
  LinePaintOptions,
} from "../GoogleMap/BaseMap";

export const MARKET_QUERY = gql`
  query market($name: String!) {
    market(name: $name) {
      id
      name
      position {
        lat
        lng
      }
      externalId
      seoString
      submarkets(visibility: VISIBLE) {
        id
        name
        geojson
        seoString
        position {
          lat
          lng
        }
        externalId
        neighborhoods(visibility: [VISIBLE, HIDDEN]) {
          id
          name
          geojson
          parameterizedName
          yearlyPricePerFoot
          externalId
          visibility
          seoString
          submarketsOfNhood: submarkets {
            id
            name
            parameterizedName
            seoString
            externalId
          }
        }
      }
      cities {
        id
        name
        position
        externalId
        neighborhoods {
          id
          name
          geojson
          parameterizedName
          yearlyPricePerFoot
          externalId
        }
      }
    }
  }
`;

type Props = {
  selectedMarket: market_market;
  selectedNeighborhoods?: market_market_submarkets_neighborhoods[];
  selectedSubmarkets?: market_market_submarkets[];
  mapClassName: string;
  hideMobileMap?: boolean;
  onMarketChange?: (market: market_market) => void;
  onSubmarketChange: (
    sMarket: market_market_submarkets,
    checked: boolean
  ) => void;
  additionalMapOptions: object;
  onNeighborhoodChange: (
    neighborhood: market_market_submarkets_neighborhoods,
    checked: boolean
  ) => void;
  showMarketSelector?: boolean;
  selectorsClassName: string;
  accessor: "cities" | "submarkets";
};

const defaultProps = {
  onMarketChange: () => {},
  hideMobileMap: true,
  showMarketSelector: true,
  selectedNeighborhoods: [],
  selectedSubmarkets: [],
};

const MarketLocationSelector = ({
  onSubmarketChange,
  onNeighborhoodChange,
  mapClassName,
  selectedMarket,
  selectorsClassName,
  additionalMapOptions,
  accessor,
  ...optionalProps
}: Props) => {
  const {
    onMarketChange = defaultProps.onMarketChange,
    hideMobileMap = defaultProps.hideMobileMap,
    showMarketSelector = defaultProps.showMarketSelector,
    selectedNeighborhoods = defaultProps.selectedNeighborhoods,
    selectedSubmarkets = defaultProps.selectedSubmarkets,
  } = optionalProps;
  const { data, loading, error } = useQuery(MARKET_QUERY, {
    variables: { name: selectedMarket.name },
    fetchPolicy: "cache-first",
  });

  if (error) throw error;

  if (loading) return <Spinner />;

  const allNeighborhoods =
    data.market[accessor].reduce(
      (acc: [], area: market_market_cities | market_market_submarkets) => [
        ...acc,
        ...area.neighborhoods,
      ],
      []
    ) || [];

  const center = maplibre.LngLat.convert(
    data?.market?.position || SQUAREFOOT_HQ_COORDS
  );

  const mapClassNames = classNames(styles.map, mapClassName, {
    [styles.hideMap]: hideMobileMap,
  });

  return (
    <div className={styles.container}>
      <div className={classNames(styles.selectors, selectorsClassName)}>
        {showMarketSelector && (
          <MarketSelector
            name="market-selector"
            selectedMarket={data.market}
            onMarketChange={onMarketChange}
            className={styles.marketSelector}
          />
        )}
        {data.market[accessor].length > 0 && (
          <SubmarketNeighborhoodSelector
            subMarkets={data.market[accessor]}
            onSubmarketChange={onSubmarketChange}
            onNeighborhoodChange={onNeighborhoodChange}
            selectedNeighborhoods={selectedNeighborhoods}
            selectedSubmarkets={selectedSubmarkets}
            allNeighborhoods={allNeighborhoods}
          />
        )}
      </div>
      <BaseMap
        autoCenter
        className={mapClassNames}
        initialViewState={{ latitude: center.lat, longitude: center.lng }}
        style={{ height: "auto" }}
      >
        {selectedNeighborhoods.map((b) => (
          <Source key={b.name} id={b.name} type="geojson" data={b.geojson}>
            <Layer id={`${b.name}-fill`} type="fill" paint={FillPaintOptions} />
            <Layer id={`${b.name}-line`} type="line" paint={LinePaintOptions} />
          </Source>
        ))}
        {selectedSubmarkets.map((b) => (
          <Source key={b.name} id={b.name} type="geojson" data={b.geojson}>
            <Layer id={`${b.name}-fill`} type="fill" paint={FillPaintOptions} />
            <Layer id={`${b.name}-line`} type="line" paint={LinePaintOptions} />
          </Source>
        ))}
      </BaseMap>
    </div>
  );
};

MarketLocationSelector.defaultProps = defaultProps;

export default MarketLocationSelector;
